import { TImageVariant } from 'typings/api-media';
import {
  Component,
  Input,
  EventEmitter,
  OnChanges,
  Output,
  OnDestroy,
  ContentChild,
  ElementRef,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { IMediaItem } from 'typings/section-types';
import { isEqual, isUndefined } from 'lodash';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, firstValueFrom, takeUntil, tap } from 'rxjs';
import { SelectMediaComponent } from 'src/components/shared/select-media/select-media.component';
import { DoenKidsGenericApiProvider } from 'src/providers/generic.provider';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { AssetService } from 'src/api/media/asset/asset.service';
import { IMedia, IMediaOriginType } from 'typings/doenkids/doenkids';
import { MediaOriginTypeQuery } from 'src/api/generic/media-origin-type/media-origin-type.query';
import { FormControl } from '@angular/forms';
import { ThemeService } from 'src/app/core/services/theme.service';

@Component({
    selector: 'app-doenkids-image',
    templateUrl: './doenkids-image.component.html',
    styleUrls: ['./doenkids-image.component.scss'],
    standalone: false
})
export class DoenkidsImageComponent implements OnChanges, OnDestroy {
  @Input()
    preview = false;

  @Input()
    previewVariant: TImageVariant = 'small';

  @Input()
    maxWidth: number;

  @Input()
    maxHeight: number;

  @Input()
    mediaItem: IMediaItem | string | File;

  @Input()
    showOriginType: boolean = false;

  @Input()
    canReplaceImage: boolean = false;

  @Input()
    isRequired: boolean = false;

  @Input()
    defaultPlaceHolder: string;

  @Output()
    loaded = new EventEmitter();

  @ContentChild(SelectMediaComponent, { read: ElementRef })
    selectMedia;

  @ViewChild('imgTag') imgTag: ElementRef;

  private destroyed$ = new Subject();

  private selectedURI$ = new BehaviorSubject<string>('');

  public dedupedSelectedUri$: Observable<string>;

  public hover: boolean;

  public isAdmin$: Observable<boolean>;

  private media: IMedia;

  /** The source picker is disabled while a save runs, so only one save is ever in flight */
  public savingOriginType = false;

  public originTypeControl = new FormControl('unknown');

  public originTypeOptions$: Observable<IMediaOriginType[]>;

  constructor(
    private genericBaseApi$: DoenKidsGenericApiProvider,
    private $asset: AssetService,
    private $mediaOriginType: MediaOriginTypeQuery,
    private themeService: ThemeService,
    $session: DoenkidsSessionProvider,
  ) {
    this.isAdmin$ = $session.isAdmin$;
    this.originTypeOptions$ = this.$mediaOriginType.selectOptions();
    this.defaultPlaceHolder = this.themeService.activityPlaceholderUrl;
  }

  async ngOnInit() {
    const isAdmin = await firstValueFrom(this.isAdmin$);

    this.setDedupedUri();

    this.selectedURI$.next(this.determineUrl());
    if (isAdmin && this.showOriginType) {
      this.fetchMedia();

      this.originTypeControl.valueChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged(isEqual)).subscribe((originType) => {
        if (this.media) {
          let originTypeId = null;
          if (originType !== 'unknown') {
            const mediaOriginType = this.$mediaOriginType.getTypeForName(originType);

            if (mediaOriginType) {
              originTypeId = mediaOriginType.id;
            }
          }

          if (originTypeId !== this.media.media_origin_type_id) {
            this.saveOriginType(originTypeId);
          }
        }
      });
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.defaultPlaceHolder && !changes.defaultPlaceHolder.firstChange) {
      if (!changes.defaultPlaceHolder.currentValue) {
        this.defaultPlaceHolder = this.themeService.activityPlaceholderUrl;
      }
      this.selectedURI$.next(this.determineUrl());
    }

    if (changes.mediaItem && !changes.mediaItem.firstChange) {
      const previousUuid = this.extractUuid(changes.mediaItem.previousValue);
      const currentUuid = this.extractUuid(changes.mediaItem.currentValue);

      if (this.canReplaceImage || previousUuid !== currentUuid) {
        this.selectedURI$.next(this.determineUrl());
        const isAdmin = await firstValueFrom(this.isAdmin$);
        if (isAdmin && this.showOriginType) {
          this.fetchMedia();
        }
      }
    }

    if (changes.canReplaceImage) {
      this.setDedupedUri();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(undefined);
  }

  public imageLoaded() {
    this.loaded.emit();
  }

  setDedupedUri() {
    if (this.canReplaceImage) {
      this.dedupedSelectedUri$ = this.selectedURI$.pipe(
        tap((uri) => {
          if (this.imgTag) {
            const element = (this.imgTag.nativeElement as HTMLImageElement);
            element.src = '';
            element.src = uri;
          }
        }),
      );
    } else {
      this.dedupedSelectedUri$ = this.selectedURI$.pipe(distinctUntilChanged(isEqual));
    }
  }

  private isMediaItem(mediaItem: IMediaItem | File) {
    const hasUuid = !isUndefined((mediaItem as IMediaItem).uuid);
    const hasDescription = !isUndefined((mediaItem as IMediaItem).description);
    const hasCaption = !isUndefined((mediaItem as IMediaItem).caption);

    return hasUuid || hasDescription || hasCaption;
  }

  getMediaUuid() {
    return this.extractUuid(this.mediaItem);
  }

  private extractUuid(mediaItem: IMediaItem | string | File) {
    if (typeof mediaItem === 'string' && !mediaItem.startsWith('http')) {
      return mediaItem;
    } else if (mediaItem && !(typeof mediaItem === 'string') && this.isMediaItem(mediaItem)) {
      return (mediaItem as IMediaItem).uuid;
    }
    return null;
  }

  async fetchMedia() {
    const uuid = this.getMediaUuid();
    this.media = uuid ? await this.$asset.fetchByUuid(uuid) : null;

    const originType = this.getOriginType();
    this.originTypeControl.setValue(originType);
  }

  /**
   * Saves the origin type. Media-api burns the AI label into the image files and
   * gives a relabelled image new file names, so the image is reloaded with a new
   * `t` to show the label. A failed save (already toasted) puts the picker back.
   */
  private async saveOriginType(originTypeId: number | null) {
    this.savingOriginType = true;
    this.originTypeControl.disable({ emitEvent: false });

    const newMedia = { ...this.media, media_origin_type_id: originTypeId } as IMedia;
    const saved = await firstValueFrom(this.$asset.update(newMedia), { defaultValue: null }).catch(() => null);

    this.originTypeControl.enable({ emitEvent: false });
    this.savingOriginType = false;

    // The image was changed meanwhile, so the result belongs to the previous one.
    if (this.media?.id !== newMedia.id) {
      return;
    }

    if (saved) {
      this.media = saved;
      this.reloadImage();
    } else {
      this.originTypeControl.setValue(this.getOriginType());
    }
  }

  /** Reloads the image under a new `t`, as the browser keeps showing the cached image for an unchanged src. */
  reloadImage() {
    this.selectedURI$.next(this.determineUrl(true));
  }

  getOriginType() {
    if (this.media) {
      return this.$mediaOriginType.getTypeForId(this.media.media_origin_type_id)?.name ?? 'unknown';
    } else if (typeof this.mediaItem === 'string' && this.mediaItem.startsWith('http')) {
      return 'eigen';
    }

    return 'unknown';
  }

  determineUrl(cacheBust = this.canReplaceImage) {
    const searchParams = new URLSearchParams();
    if (this.preview) {
      searchParams.set('variant', this.previewVariant);
    }
    // Add cache busting for replaced and relabelled images
    if (cacheBust) {
      searchParams.set('t', Date.now().toString());
    }
    const mediaUuid = this.getMediaUuid();
    if (mediaUuid) {
      return `${this.genericBaseApi$.API_END_POINT}/media/image/uuid/${mediaUuid}?${searchParams.toString()}`;
    } else if (this.mediaItem && typeof this.mediaItem === 'string') {
      return this.mediaItem;
    } else if (this.mediaItem && (typeof this.mediaItem !== 'string') && !this.isMediaItem(this.mediaItem)) {
      return URL.createObjectURL(this.mediaItem as File);
    }
    return this.defaultPlaceHolder;
  }

  onHover(hovering = false) {
    this.hover = hovering;
  }
}
